/*
  MSS seasonal page - buy cards, mix & save bar, ship-by countdown.
  Loaded with defer by snippets/mss-season-styles. No libraries, no work until the page is idle or a shopper clicks.
  Adds go through Shopify's own /cart/add.js, then the theme's cart drawer + cart count are refreshed with the
  sections returned in the same request (one round trip), exactly like the theme's own quick add.
*/
(() => {
  if (window.mssSeason) return;
  window.mssSeason = true;

  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const fire = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
  const settingsOf = (el) => el.closest('[data-mss-season]')?.dataset || {};

  /* ---------- cart ---------- */
  /* One shared cart count for the page: the deal bar and the team grid both read it, refreshed on every cart event. */
  const cartCount = { n: null, t: null };
  function refreshCartCount() {
    clearTimeout(cartCount.t);
    cartCount.t = setTimeout(async () => {
      try {
        const cart = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        cartCount.n = cart.item_count || 0;
        fire('mss:cart-count', { count: cartCount.n });
      } catch (e) {}
    }, 150);
  }
  ['mss:cart-changed', 'cart:item-added', 'cart:refresh', 'line-item:change:end'].forEach((n) => document.addEventListener(n, refreshCartCount));
  window.addEventListener('pageshow', (e) => { if (e.persisted) refreshCartCount(); });
  (window.requestIdleCallback || ((f) => setTimeout(f, 1500)))(refreshCartCount);
  function swapSection(id, selector, html) {
    const host = document.getElementById(id);
    if (!host || !html) return;
    const target = host.querySelector(selector) || host;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (fresh) target.innerHTML = fresh.innerHTML;
  }

  async function addToCart(items, openDrawer) {
    const drawer = document.getElementById('Cart-Drawer');
    const body = { items: items };
    if (drawer) {
      body.sections = 'cart-drawer,cart-bubble';
      body.sections_url = window.location.pathname;
    }
    const res = await fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');

    if (data.sections) {
      swapSection('Cart-Drawer', '.cart-drawer', data.sections['cart-drawer']);
      swapSection('cart-drawer-toggle', '.thb-item-count', data.sections['cart-bubble']);
      if (drawer && typeof drawer.removeProductEvent === 'function') drawer.removeProductEvent();
      if (openDrawer && drawer && typeof drawer.open === 'function') drawer.open();
    } else {
      fire('cart:refresh');
    }
    fire('cart:item-added', { product: data.items ? data.items[0] : data });
    fire('mss:cart-changed');
    return data;
  }

  /* ---------- buy card ---------- */
  class SeasonCard extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.form = this.querySelector('form');
      this.button = this.querySelector('.mss-sc__add');
      this.label = this.querySelector('[data-label]');
      this.msg = this.querySelector('[data-msg]');
      this.fit = this.querySelector('[data-fit]');
      this.sizeSet = this.querySelector('[data-size]');
      this.team = this.querySelector('[data-team]');
      this.sum = this.querySelector('[data-team-sum]');
      this.img = this.querySelector('.mss-sc__media img');
      if (this.img) this.img0 = { src: this.img.getAttribute('src'), srcset: this.img.getAttribute('srcset') || '' };
      try { this.variants = JSON.parse(this.querySelector('[data-variants]').textContent); } catch (e) { this.variants = []; }
      this.addLabel = this.label ? this.label.textContent : 'Add to cart';
      this.form.addEventListener('change', () => this.update());
      this.qty = this.querySelector('[data-qty] input');
      this.form.addEventListener('input', (e) => { if (e.target.matches('[data-team] input') || e.target === this.qty) this.update(); });
      this.form.addEventListener('click', (e) => {
        const step = e.target.closest('[data-step]');
        if (!step) return;
        const input = step.parentNode.querySelector('input');
        const min = Number(input.min) || 0;
        input.value = Math.max(min, Math.min(Number(input.max) || 999, (Number(input.value) || min) + Number(step.dataset.step)));
        this.update();
      });
      if (this.team) {
        this.team.querySelectorAll('[data-row-fit]').forEach((el) => { el.textContent = this.fitText(el.closest('[data-value]').dataset.value); });
        document.addEventListener('mss:cart-count', () => this.updateTeam(this.chosen()));
      }
      this.form.addEventListener('submit', (e) => this.submit(e));
      this.update();
    }

    chosen() {
      const picks = [];
      this.form.querySelectorAll('[data-option]').forEach((el) => {
        const i = Number(el.dataset.option);
        if (el.matches('fieldset')) {
          const on = el.querySelector('input:checked');
          picks[i] = on ? on.value : null;
        } else if (el.matches('[data-team]')) {
          picks[i] = null;
        } else {
          picks[i] = el.value;
        }
      });
      return picks;
    }

    match(picks) {
      return this.variants.find((v) => v.o.every((val, i) => picks[i] == null || picks[i] === val));
    }

    update() {
      const picks = this.chosen();
      if (this.team) return this.updateTeam(picks);
      const sizeIndex = this.sizeSet ? Number(this.sizeSet.dataset.option) : -1;

      if (this.sizeSet) {
        this.sizeSet.classList.remove('is-needed');
        this.sizeSet.querySelectorAll('input').forEach((input) => {
          const test = picks.slice();
          test[sizeIndex] = input.value;
          const v = this.variants.find((x) => x.o.every((val, i) => test[i] == null || test[i] === val));
          input.disabled = !v || !v.a;
          if (input.disabled && input.checked) input.checked = false;
        });
        picks[sizeIndex] = (this.sizeSet.querySelector('input:checked') || {}).value || null;
      }

      const exact = picks.every((p) => p != null) ? this.match(picks) : null;
      const shown = exact || this.match(picks);
      if (shown) {
        const price = this.querySelector('[data-price]');
        if (price) price.innerHTML = shown.p + (shown.c ? ' <s>' + shown.c + '</s>' : '');
        const pic = this.variants.find((v) => v.i && v.o.every((val, i) => i === sizeIndex || picks[i] == null || picks[i] === val));
        if (this.img && pic && this.img.dataset.v !== pic.i) {
          this.img.dataset.v = pic.i;
          this.img.srcset = '';
          this.img.src = pic.s || pic.i;
        } else if (this.img && !pic && this.img.dataset.v) {
          delete this.img.dataset.v;
          this.img.srcset = this.img0.srcset;
          this.img.src = this.img0.src;
        }
      }

      const anyAvailable = this.variants.some((v) => v.a && v.o.every((val, i) => i === sizeIndex || picks[i] == null || picks[i] === val));
      this.button.disabled = !anyAvailable;
      let qty = 1;
      if (this.qty) {
        if (exact && exact.q != null) this.qty.max = Math.max(1, exact.q); else this.qty.max = 999;
        qty = Math.max(1, Math.min(Number(this.qty.max), Number(this.qty.value) || 1));
        this.qty.value = qty;
      }
      const s = settingsOf(this);
      if (this.label) this.label.textContent = !anyAvailable ? (s.soldout || 'Sold out') : qty > 1 ? (s.qtyadd || 'Add [n] to cart').replace('[n]', qty) : this.addLabel;

      if (this.fit) {
        this.fit.classList.remove('is-alert');
        const size = sizeIndex > -1 ? picks[sizeIndex] : null;
        this.fit.textContent = size ? this.fitText(size) : '';
      }
      this.variant = exact;
    }

    /* Team grid: a quantity per size, all sizes added in one click. */
    updateTeam(picks) {
      const sizeIndex = Number(this.team.dataset.option);
      const s = settingsOf(this);
      if (this.fit) { this.fit.textContent = ''; this.fit.classList.remove('is-alert'); }
      const lines = [];
      let pairs = 0, cents = 0, sample = null;
      this.team.querySelectorAll('[data-value]').forEach((row) => {
        const test = picks.slice();
        test[sizeIndex] = row.dataset.value;
        const v = this.variants.find((x) => x.o.every((val, i) => test[i] == null || test[i] === val));
        const input = row.querySelector('input');
        const ok = v && v.a;
        row.classList.toggle('is-out', !ok);
        input.disabled = !ok;
        row.querySelectorAll('[data-step]').forEach((b) => { b.disabled = !ok; });
        if (!ok) { input.value = 0; return; }
        if (v.q != null) { input.max = Math.max(0, v.q); if (Number(input.value) > v.q) input.value = v.q; }
        const n = Number(input.value) || 0;
        sample = sample || v;
        if (n > 0) { pairs += n; cents += n * v.pc; lines.push({ id: v.id, quantity: n }); }
      });
      this.items = lines;
      this.variant = null;
      this.button.disabled = !sample;
      if (this.label) {
        this.label.textContent = !sample ? (s.soldout || 'Sold out') : pairs > 0 ? (s.teamadd || 'Add [n] pairs to cart').replace('[n]', pairs) : this.addLabel;
      }
      const pic = this.variants.find((v) => v.i && v.o.every((val, i) => i === sizeIndex || picks[i] == null || picks[i] === val));
      if (this.img && pic && this.img.dataset.v !== pic.i) { this.img.dataset.v = pic.i; this.img.srcset = ''; this.img.src = pic.s || pic.i; }
      if (this.sum) {
        if (pairs === 0) { this.sum.innerHTML = ''; return; }
        const money = (c) => (sample.p || '$0.00').replace(/[\d][\d.,]*/, (c / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','));
        const inCart = cartCount.n || 0;
        const total = pairs + inCart;
        let html = '<strong>' + pairs + ' ' + (s.pairs || 'pairs') + '</strong> &middot; ' + money(cents);
        if (inCart) html += ' &middot; ' + (s.incart || '[n] already in cart').replace('[n]', inCart);
        const tiers = ((document.querySelector('mss-season-deal') || {}).dataset || {}).tiers;
        if (tiers) {
          const list = tiers.split(',').map((t) => t.split(':').map(Number)).filter((t) => t[0] > 0).sort((a, b) => a[0] - b[0]);
          const now = list.filter((t) => total >= t[0]).pop();
          const next = list.find((t) => total < t[0]);
          if (now) html += ' &middot; <em>' + now[1] + '% off at checkout</em>';
          if (next) html += ' &middot; add ' + (next[0] - total) + ' more for ' + next[1] + '% off';
        }
        this.sum.innerHTML = html;
      }
    }

    fitText(size) {
      const lines = (settingsOf(this).fit || '').split('\n');
      for (const line of lines) {
        const at = line.indexOf('=');
        if (at > 0 && line.slice(0, at).trim().toLowerCase() === size.toLowerCase()) return line.slice(at + 1).trim();
      }
      return '';
    }

    async submit(e) {
      e.preventDefault();
      if (this.button.disabled) return;
      this.msg.textContent = '';
      if (this.team && !(this.items && this.items.length)) {
        this.fit.textContent = settingsOf(this).pick || 'Pick a size first';
        this.fit.classList.add('is-alert');
        const first = this.team.querySelector('input:not(:disabled)');
        if (first) first.focus({ preventScroll: true });
        return;
      }
      if (!this.team && !this.variant) {
        if (this.sizeSet) {
          this.sizeSet.classList.remove('is-needed');
          void this.sizeSet.offsetWidth;
          this.sizeSet.classList.add('is-needed');
          this.fit.textContent = settingsOf(this).pick || 'Pick a size';
          this.fit.classList.add('is-alert');
          const first = this.sizeSet.querySelector('input:not(:disabled)');
          if (first) first.focus({ preventScroll: true });
        }
        return;
      }
      const s = settingsOf(this);
      this.button.classList.add('is-busy');
      try {
        await addToCart(this.team ? this.items : [{ id: this.variant.id, quantity: this.qty ? Number(this.qty.value) || 1 : 1 }], s.after === 'drawer');
        if (this.team) { this.team.querySelectorAll('input').forEach((i) => { i.value = 0; }); this.updateTeam(this.chosen()); }
        if (this.qty) { this.qty.value = 1; this.update(); }
        this.button.classList.add('is-done');
        this.label.textContent = s.added || 'Added!';
        if (!document.getElementById('Cart-Drawer') || s.after !== 'drawer') {
          this.msg.innerHTML = '<a href="' + root + 'cart">' + (s.viewcart || 'View cart') + ' &rarr;</a>';
        }
        clearTimeout(this.t);
        this.t = setTimeout(() => {
          this.button.classList.remove('is-done');
          this.label.textContent = this.addLabel;
        }, 2200);
      } catch (err) {
        this.msg.textContent = err.message;
      } finally {
        this.button.classList.remove('is-busy');
      }
    }
  }
  if (!customElements.get('mss-season-card')) customElements.define('mss-season-card', SeasonCard);

  /* ---------- mix & save bar ---------- */
  class SeasonDeal extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.tiers = (this.dataset.tiers || '')
        .split(',')
        .map((t) => t.split(':').map(Number))
        .filter((t) => t[0] > 0 && t[1] > 0)
        .sort((a, b) => a[0] - b[0]);
      this.text = this.querySelector('[data-text]');
      this.fill = this.querySelector('[data-fill]');
      this.querySelector('[data-close]')?.addEventListener('click', () => {
        this.closed = true;
        this.classList.remove('is-on');
        try { sessionStorage.setItem('mss-season-deal-closed', '1'); } catch (e) {}
      });
      try { this.closed = sessionStorage.getItem('mss-season-deal-closed') === '1'; } catch (e) {}
      document.addEventListener('mss:cart-count', (e) => this.render(e.detail.count));
      if (cartCount.n != null) this.render(cartCount.n);
    }

    render(count) {
      const d = this.dataset;
      const fill = (t, n, p) => t.replace('[count]', count).replace('[n]', n).replace('[p]', p);
      const current = this.tiers.filter((t) => count >= t[0]).pop();
      const next = this.tiers.find((t) => count < t[0]);
      let html;
      if (!next) html = fill(d.max || '', 0, current ? current[1] : 0);
      else if (current) html = fill(d.mid || '', next[0] - count, next[1]).replace('[now]', current[1]);
      else html = fill(d.start || '', next[0] - count, next[1]);
      this.text.innerHTML = html;
      const goal = next ? next[0] : count || 1;
      if (this.fill) this.fill.style.width = Math.min(100, (count / goal) * 100) + '%';
      this.classList.toggle('is-on', count > 0 && !this.closed && this.tiers.length > 0);
    }
  }
  if (!customElements.get('mss-season-deal')) customElements.define('mss-season-deal', SeasonDeal);

  /* ---------- custom colour picker (sock drawing recolours, link + proof form carry the picks) ---------- */
  class SeasonColors extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.svg = this.querySelector('svg');
      this.text = this.querySelector('[data-sock-text]');
      this.team = this.querySelector('[data-team]');
      this.addEventListener('change', () => this.update());
      this.addEventListener('input', (e) => { if (e.target === this.team) this.update(); });
      this.update();
    }
    pick(kind) {
      const on = this.querySelector('input[name$="-' + kind + '"]:checked');
      return on ? { hex: on.value, name: on.dataset.name } : null;
    }
    update() {
      const main = this.pick('main'), accent = this.pick('accent');
      const name = (this.team && this.team.value.trim()) || '';
      if (main) this.svg.style.setProperty('--c1', main.hex);
      if (accent) this.svg.style.setProperty('--c2', accent.hex);
      if (main && accent) {
        const lum = (h) => { const n = parseInt(h.slice(1), 16); return (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255; };
        const same = Math.abs(lum(main.hex) - lum(accent.hex)) < 0.18;
        this.svg.style.setProperty('--ct', same ? (lum(main.hex) > 0.5 ? '#111111' : '#ffffff') : accent.hex);
      }
      if (this.text) {
        const label = (name || this.dataset.defaultName || '').toUpperCase().slice(0, 14);
        this.text.textContent = label;
        this.text.setAttribute('textLength', String(Math.min(300, Math.max(60, label.length * 22))));
      }
      this.querySelectorAll('[data-out]').forEach((o) => { const p = o.dataset.out === 'main' ? main : accent; if (p) o.textContent = p.name; });
      const scope = this.closest('.mss-scus') || document;
      const colors = main && accent ? main.name + ' / ' + accent.name : '';
      scope.querySelectorAll('[data-proof-colors]').forEach((i) => { i.value = colors; });
      scope.querySelectorAll('[data-proof-team]').forEach((i) => { i.value = name; });
      scope.querySelectorAll('[data-custom-link]').forEach((a) => {
        if (!a.dataset.base) a.dataset.base = a.getAttribute('href');
        const q = new URLSearchParams();
        if (main) q.set('main', main.name);
        if (accent) q.set('accent', accent.name);
        if (name) q.set('team', name);
        const base = a.dataset.base;
        a.href = base + (base.includes('?') ? '&' : '?') + q.toString();
      });
    }
  }
  if (!customElements.get('mss-season-colors')) customElements.define('mss-season-colors', SeasonColors);

  /* ---------- ship-by countdown ---------- */
  class SeasonCountdown extends HTMLElement {
    connectedCallback() {
      this.end = Number(this.dataset.end) * 1000;
      this.out = this.querySelector('[data-left]');
      if (!this.end || !this.out) return;
      this.tick();
      this.timer = setInterval(() => this.tick(), 30000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      const ms = this.end - Date.now();
      if (ms <= 0) {
        clearInterval(this.timer);
        const after = this.dataset.after;
        if (after) this.innerHTML = '<span>' + after + '</span>';
        else this.hidden = true;
        return;
      }
      const m = Math.floor(ms / 60000);
      const days = Math.floor(m / 1440), hrs = Math.floor((m % 1440) / 60), mins = m % 60;
      this.out.textContent = (days ? days + 'd ' : '') + hrs + 'h ' + String(mins).padStart(2, '0') + 'm';
    }
  }
  if (!customElements.get('mss-season-countdown')) customElements.define('mss-season-countdown', SeasonCountdown);
})();
